import { Link } from "react-router-dom";
import { fileUrl } from "../lib/api";
import AnimatedImage from "./AnimatedImage";

/** Render a single homepage content block. */
export const HomepageBlock = ({ block }) => {
    if (!block || !block.type) return null;
    const d = block.data || {};
    switch (block.type) {
        case "heading":
            return (
                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid={`hb-heading-${block.id}`}>
                    {d.eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-primary mb-4">{d.eyebrow}</div>}
                    <h2 className="font-display text-4xl sm:text-5xl mb-4 max-w-3xl mx-auto">{d.title}</h2>
                    {d.subtitle && <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{d.subtitle}</p>}
                </section>
            );
        case "text":
            return (
                <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid={`hb-text-${block.id}`}>
                    {d.title && <h3 className="font-display text-3xl mb-5">{d.title}</h3>}
                    <p className="text-base leading-relaxed text-foreground/85 whitespace-pre-line">{d.body}</p>
                </section>
            );
        case "image_text": {
            const reversed = d.layout === "image-right";
            return (
                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid={`hb-imgtxt-${block.id}`}>
                    <div className={`grid lg:grid-cols-2 gap-10 items-center ${reversed ? "lg:[&>:first-child]:order-2" : ""}`}>
                        <div className="aspect-[4/3] rounded-sm overflow-hidden">
                            {d.image_url
                                ? <AnimatedImage src={d.image_url} alt={d.title} className="w-full h-full" />
                                : <div className="w-full h-full bg-muted" />}
                        </div>
                        <div>
                            {d.title && <h3 className="font-display text-3xl mb-4">{d.title}</h3>}
                            <p className="text-base leading-relaxed text-foreground/85 whitespace-pre-line">{d.body}</p>
                        </div>
                    </div>
                </section>
            );
        }
        case "quote":
            return (
                <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center" data-testid={`hb-quote-${block.id}`}>
                    <div className="font-display text-3xl sm:text-4xl leading-tight mb-6 text-foreground">"{d.quote}"</div>
                    {(d.attribution || d.role) && (
                        <div className="text-sm text-muted-foreground">
                            {d.attribution && <span className="font-medium text-foreground">{d.attribution}</span>}
                            {d.role && <span> · {d.role}</span>}
                        </div>
                    )}
                </section>
            );
        case "stats":
            return (
                <section className="bg-muted/40 py-16" data-testid={`hb-stats-${block.id}`}>
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        {d.eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-primary mb-6">{d.eyebrow}</div>}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                            {(d.stats || []).map((s, i) => (
                                <div key={i} className="border-l-2 border-primary pl-4">
                                    <div className="font-display text-5xl mb-1">{s.value}</div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            );
        case "cta":
            return (
                <section className="bg-primary text-primary-foreground" data-testid={`hb-cta-${block.id}`}>
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-8">
                            {d.eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-primary-foreground/70 mb-3">{d.eyebrow}</div>}
                            <h2 className="font-display text-4xl mb-2">{d.title}</h2>
                            {d.body && <p className="text-primary-foreground/80 max-w-2xl">{d.body}</p>}
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            {(d.button_link || "").startsWith("http") ? (
                                <a href={d.button_link} target="_blank" rel="noreferrer" className="inline-flex items-center px-7 py-3.5 rounded-sm bg-background text-foreground text-sm font-medium">{d.button_label || "Learn more"}</a>
                            ) : (
                                <Link to={d.button_link || "/contact"} className="inline-flex items-center px-7 py-3.5 rounded-sm bg-background text-foreground text-sm font-medium">{d.button_label || "Learn more"}</Link>
                            )}
                        </div>
                    </div>
                </section>
            );
        case "spacer":
            return <div style={{ height: `${parseInt(d.size) || 60}px` }} aria-hidden="true" />;
        default:
            return null;
    }
};

export default HomepageBlock;
